import logger from "./services/logger.js";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./css/App.css";

import AuthService from "./services/auth.service";
import EventBus from "./common/EventBus";

import Navigation from "./components/navigation";
import Header from "./components/common/Header";
import Banner from "./components/common/Banner";

import Login from "./components/login.component";
import Register from "./components/register.component";
import ForgotPassword from "./components/forgotpassword";
import ResetPassword from "./components/resetpassword";

import Home from "./components/home.component";
import Welcome from "./components/welcome/welcome";
import Profile from "./components/profile.component";

import BadgeDetailPage from "./components/animation/BadgeDetailPage";

import BoardUser from "./components/board-user.component";
import BoardFreemium from "./components/board-freemium.component";
import BoardAssure from "./components/board-assure.component";
import ProductList from "./components/maintenance/productlist";
import MaintenanceCard from "./components/maintenance/maintenancecard";
import MaintenanceCardDetail from "./components/maintenance/maintenancecarddetail";
import ViewCalendar from "./components/calendar/viewcalendar";
import SmartFix from "./components/smartfix/smartform";
import ViewNotif from "./components/notification/viewnotif";
import ApplianceList from "./components/portfolio/appliancelist";
import Univers from "./components/portfolio/univers";
import UniverDetail from "./components/portfolio/univerdetail";
import UniverCRUDForm from "./components/common/UniverCRUDForm";

import CarboTool from "./components/tool/CarboTool";
import CarboMap from "./components/tool/CarboMap";
import CallPlanning from "./components/tool/CallPlanning";

import ApplianceDetail from "./components/portfolio/appliancedetail";
import ApplianceUpdateForm from "./components/portfolio/ApplianceUpdateForm";
import ApplianceAdd from "./components/portfolio/applianceadd";

import RepairPage from "./components/repair/RepairPage";

import AddDevicePage from "./components/newdevice/DeviceAddPage";

import AppSearch from "./components/portfolio/appsearch";
import ApplianceReplace from "./components/replaceappliance";
import ProductReplace from "./components/replaceproduct";
import ActionSelect from "./components/secondlife/actionselect";
import Give from "./components/secondlife/give";
import Sell from "./components/secondlife/sell";
import Recycle from "./components/secondlife/recycle";
import OptionSelect from "./components/renew/optionselect";
import CircularTip from "./components/renew/circulartip";
import FundSearch from "./components/renew/fundsearch";
import RenewOffer from "./components/renew/renewoffer";
import AuthVerify from "./common/auth-verify";

const PUBLIC_ROUTES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/welcome"
];

const isPublicRoute = (path) => {
  return (
    PUBLIC_ROUTES.includes(path) ||
    path.startsWith("/public/badges/")
  );
};

const ContainerWrapper = ({ children }) => {
  const location = useLocation();
  const fullPageRoutes = ["/", "/login", "/register", "/welcome"];
  const isFullPage = fullPageRoutes.includes(location.pathname);
  return isFullPage ? children : (
    <div className="container app-container container-fluid">
      {children}
      <br/>
      <br/>
    </div>
  );
};

function App() {
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);
  const [showNavBar, setShowNavBar] = useState(true);
  //const [showFreemiumBoard, setShowFreemiumBoard] = useState(false);
  //const [showAssureBoard, setShowAssureBoard] = useState(true);
  const [showHeader, setshowHeader] = useState(true);
  const location = useLocation();

 
  const logOut = useCallback(() => {
    AuthService.logout();
  }, []);


  const didRedirect = useRef(false);
  const prevUserRef = useRef(null);

  useEffect(() => {
  switch (location.pathname) {
    case '/login':
    case '/register':
    case '/forgot-password':
    case '/reset-password':
    case '/welcome':
      setShowNavBar(false);
      setshowHeader(false);
      setShowBanner(false);
      break;
    case '/profile':
    case '/badge':
    case '/entretien/fiche':
    case '/entretien/fichedetail':
      setshowHeader(false);
      setShowBanner(false);
      setShowNavBar(true);
      break;
    case '/home':
      setshowHeader(true);
      setShowBanner(true);
      setShowNavBar(true);
      break;
    default:
      setshowHeader(true);
      setShowBanner(false);
      setShowNavBar(true);
      break;
  }
}, [location.pathname]);



  useEffect(() => {
    const user = AuthService.getCurrentUser();

    // ne setState que si l'utilisateur a réellement changé (évite boucle)
    if (user) {
      if (!prevUserRef.current || prevUserRef.current.uuid !== user.uuid) {
        prevUserRef.current = user;
      }
      didRedirect.current = false;
    } else if (isPublicRoute(location.pathname)) {
      // Page publique, pas besoin de login
      didRedirect.current = false;
    } else if (!didRedirect.current) {
      didRedirect.current = true;
      logOut();
      navigate("/login");
    }

    const prevOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      logger.log("Global error captured:", message);
      // utiliser la variable 'user' (stable dans cet effet) plutôt que currentUser
      logger.log("current user", user);
      // éviter toute navigation automatique depuis un handler global d'erreur
    };

    EventBus.on("logout", logOut);
    return () => {
      // retirer correctement le listener et restaurer onerror
      EventBus.remove("logout", logOut);
      window.onerror = prevOnError;
    };
  }, [location.pathname, logOut, navigate]); // currentUser retiré intentionnellement

  
  return (
    <>
      {showHeader && <Header />}
      {showBanner && <Banner />} 
      {showNavBar && <Navigation/>}
      <ContainerWrapper>
        <Routes>
          <Route path="" element={<Login/>} />
          <Route path="/welcome" element={<Welcome/>} />
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home /> }/>
          <Route path="/entretien/fiche" element={<MaintenanceCard />} />
          <Route path="/entretien/fichedetail" element={<MaintenanceCardDetail />} />
          <Route path="/productlist" element={<ProductList />} />
          <Route path="/calendar" element={<ViewCalendar />} />
          <Route path="/notification" element={<ViewNotif />}/>
          <Route path="/inventaire/univers" element={<Univers />} />
          <Route path="/inventaire/univers/:id/detail" element={<UniverDetail />} />
          <Route path="/inventaire/univers/update" element={<UniverCRUDForm />} />
          <Route path="/inventaire/univers/appliance/fichedetail" element={<ApplianceDetail />} />
          <Route path="/inventaire/univers/appliance/update" element={<ApplianceUpdateForm />} />
          <Route path="/inventaire" element={<ApplianceList />} />
          <Route path="/inventaire/nouveau_2" element={<ApplianceAdd />} />
          <Route path="/inventaire/search" element={<AppSearch />} />
          <Route path="/new-device" element={<AddDevicePage />} />
          <Route path="/carbotool" element={<CarboTool />} />
          <Route path="/carbomap" element={<CarboMap />} />
          <Route path="/callplan" element={<CallPlanning />} />
          <Route path="/replace/appliance" element={<ApplianceReplace />} />
          <Route path="/replace/product" element={<ProductReplace />} />
          <Route path="/secondlife/actionselect" element={<ActionSelect />} />
          <Route path="/secondlife/give" element={<Give />} />
          <Route path="/secondlife/sell" element={<Sell />} />
          <Route path="/secondlife/recycle" element={<Recycle />} />
          <Route path="/renew/optionselect" element={<OptionSelect />} />
          <Route path="/renew/fundsearch" element={<FundSearch />} />
          <Route path="/renew/tip" element={<CircularTip />} />
          <Route path="/renew/offer" element={<RenewOffer />} />
          <Route path="/repair" element={<RepairPage />} />
          <Route path="/smarfix" element={<SmartFix />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />}/>
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/profile" element={<Profile />}/>
          <Route path="/user" element={<BoardUser />} />
          <Route path="/badges/:badgeId" element={<BadgeDetailPage />} />
          
          
          <Route path="/freemium" element={<BoardFreemium />} />
          <Route path="/assure" element={<BoardAssure />} />
        </Routes>
      </ContainerWrapper>
      <AuthVerify logOut={logOut} />  
    </>
  );
}

export default App;
